import { adicionarItem, atualizarItem, removerItem } from './store'
import type { ResultadoDaTarefaDeDownload, TarefaDeDownload } from './types'

export const LIMITE_DE_DOWNLOADS_SIMULTANEOS = 10

interface EntradaDaFila {
  id: string
  tarefa: TarefaDeDownload
  controlador?: AbortController
}

const aguardando: EntradaDaFila[] = []
const emExecucao = new Map<string, EntradaDaFila>()

function estaNaFila(id: string): boolean {
  return emExecucao.has(id) || aguardando.some(entrada => entrada.id === id)
}

export function enfileirarDownload(tarefa: TarefaDeDownload): string {
  if (tarefa.id && estaNaFila(tarefa.id)) return tarefa.id

  const id = adicionarItem({
    id: tarefa.id,
    titulo: tarefa.titulo,
    status: 'na-fila',
    aoTentarNovamente: undefined,
    aoCancelar: () => cancelarDownload(id),
  })

  aguardando.push({ id, tarefa })
  processarFila()

  return id
}

export function cancelarDownload(id: string): void {
  const posicao = aguardando.findIndex(entrada => entrada.id === id)

  if (posicao >= 0) {
    aguardando.splice(posicao, 1)
    return
  }

  emExecucao.get(id)?.controlador?.abort()
}

export function cancelarTodosOsDownloads(): void {
  aguardando.length = 0

  for (const entrada of emExecucao.values()) {
    entrada.controlador?.abort()
  }
}

function processarFila(): void {
  while (
    emExecucao.size < LIMITE_DE_DOWNLOADS_SIMULTANEOS &&
    aguardando.length > 0
  ) {
    const proxima = aguardando.shift()
    if (proxima) executar(proxima)
  }
}

async function executar(entrada: EntradaDaFila): Promise<void> {
  const { id, tarefa } = entrada
  const controlador = new AbortController()
  const { signal } = controlador

  entrada.controlador = controlador
  emExecucao.set(id, entrada)

  atualizarItem(id, { status: tarefa.statusInicial ?? 'baixando' })

  let resultado: ResultadoDaTarefaDeDownload = 'erro'

  try {
    resultado = await tarefa.executar({ signal })
  } catch {
    resultado = 'erro'
  } finally {
    emExecucao.delete(id)
  }

  if (!signal.aborted) {
    if (resultado === 'concluido') {
      atualizarItem(id, { status: 'concluido' })
    } else if (resultado === 'erro') {
      atualizarItem(id, {
        status: 'erro',
        aoTentarNovamente: () => enfileirarDownload({ ...tarefa, id }),
      })
    } else {
      removerItem(id)
    }
  }

  processarFila()
}
